/**
 * Whose picture is that, and may we show it at all.
 *
 * Attribution is a condition, not metadata — the reasoning at the top of
 * `src/harvest/venuehero.ts`. A CC image without its author, its licence and
 * a link back is an infringing copy, and a gallery's press image with a
 * courtesy line under it is no different in kind.
 *
 * Two rules, and both had to be learned the hard way on the same afternoon.
 *
 * No imports, so `npm test` can load it: the app's extensionless imports are
 * a Vite convenience that Node's loader does not share.
 */

/** Hosts that answer a cross-origin image request with 403.
 *
 *  Measured rather than assumed. `curl` with no referer gets 180KB of JPEG
 *  from indexberlin.com; the same URL with `-e http://localhost:5182/` gets
 *  403 and an HTML error page. That is hotlink protection, and it is a
 *  refusal: rule 4 says a refusal is reported rather than worked around, and
 *  sending no referer to get the bytes is exactly the routing-around the
 *  rule names. Copying the files into R2 sidesteps the refusal by not
 *  triggering it, which is a rights judgement about someone else's press
 *  images and is Doug's to make — the venue heroes are re-hosted because
 *  Commons licences say they may be, and a courtesy line is not a licence.
 */
export const HOTLINK_REFUSED = ['indexberlin.com']

export function hotlinkRefused(url: string): boolean {
  try {
    return HOTLINK_REFUSED.some((h) => new URL(url).hostname.endsWith(h))
  } catch {
    // A URL we cannot parse is a URL we cannot reason about.
    return true
  }
}

export interface Pictures {
  /** The row's own picture, from the source. */
  imageUrl?: string | null
  /** The storage key of the row's own picture where we hold a copy — a
   *  re-hosted press image (src/harvest/rehost.ts) or a newsletter capture.
   *  `feed.hero_key`. The drawn `src` ends in it, and the credit is then the
   *  row's own, never the venue photograph's. */
  ownKey?: string | null
  /** Printed under the picture when we have one; no longer a gate on drawing. */
  imageCredit?: string | null
  /** The venue photograph's, which carries an author and a licence. */
  venueCredit?: string | null
  venueLicense?: string | null
}

/** Is this URL a still image rather than a video the harvest mistook for one?
 *
 *  Staatsoper's "image" field has been an `.mp4` loop. Drawing that as an
 *  `<img>` fails silently and leaves a swatch where the venue hero would
 *  have shown — so a non-image URL is treated as no picture. */
export function looksLikeImage(url: string): boolean {
  try {
    const path = new URL(url).pathname.toLowerCase()
    return !/\.(mp4|webm|mov|m4v|mp3|pdf)(\?|$)/.test(path)
  } catch {
    return false
  }
}

/** May the row's own picture be drawn?
 *
 *  Needs a URL and a host that will actually serve it. Credit is still
 *  printed when we have one (`creditFor`), but classical calendars publish
 *  a performance photograph with no caption — requiring a credit that the
 *  harvest never wrote was hiding every Konzerthaus and Staatsoper still
 *  that was not already refused, which is the opposite of what the card
 *  is for. INDEX remains blocked by `hotlinkRefused`, credit or not. */
export const ownPictureUsable = (p: Pictures): boolean =>
  Boolean(p.imageUrl && looksLikeImage(p.imageUrl) && !hotlinkRefused(p.imageUrl))

/**
 * The credit for the picture that is drawn.
 *
 * Takes the resolved `src` rather than deciding again, because the first
 * version asked the row what credits it had and returned the first one — so
 * "BAUBAU © Gropius Bau, Foto: Guannan Li." appeared under a Commons
 * photograph of the Gropius Bau's dome taken by someone called Virtual-Pano.
 * Crediting the wrong photographer is worse than crediting nobody: it is a
 * false statement about who made the thing on the screen, and it would have
 * been one on every exhibition whose own picture we hold and cannot show.
 *
 * A poster gets none: TMDb asks for attribution on an about page rather than
 * beside every card.
 */
export function creditFor(
  src: string | null,
  p: Pictures,
  posterSrc: string | null = null,
): string | null {
  if (!src) return null
  if (posterSrc && src === posterSrc) return null
  if (src === p.imageUrl) return p.imageCredit ?? null
  if (p.ownKey && src.endsWith(`/${p.ownKey}`)) return p.imageCredit ?? null
  if (!p.venueCredit) return null
  return [p.venueCredit, p.venueLicense].filter(Boolean).join(' · ')
}
